import React, { useEffect, useRef, useState } from 'react'
import noUiSlider from 'nouislider'
import 'nouislider/dist/nouislider.css'
import { parseDateInput, findClosestMonthIndex } from '../../utils/dateInputParser'
import './DateRangeSlider.css'

interface MonthInfo {
    label: string
    value: number
    date: Date
}

interface DateRangeSliderProps {
    monthList: MonthInfo[]
    startIndex: number
    endIndex: number
    onChange: (startIndex: number, endIndex: number) => void
}

const DateRangeSlider: React.FC<DateRangeSliderProps> = ({
    monthList,
    startIndex,
    endIndex,
    onChange,
}) => {
    const sliderRef = useRef<HTMLDivElement>(null)
    const sliderInstance = useRef<any>(null)
    const isUpdatingProgrammatically = useRef(false)
    const [displayStart, setDisplayStart] = useState(startIndex)
    const [displayEnd, setDisplayEnd] = useState(endIndex)
    const [startInputValue, setStartInputValue] = useState('--')
    const [endInputValue, setEndInputValue] = useState('--')
    const [isStartEditing, setIsStartEditing] = useState(false)
    const [isEndEditing, setIsEndEditing] = useState(false)

    useEffect(() => {
        if (!isStartEditing) {
            setStartInputValue(monthList[displayStart]?.label || '--')
        }
    }, [displayStart, monthList, isStartEditing])

    useEffect(() => {
        if (!isEndEditing) {
            setEndInputValue(monthList[displayEnd]?.label || '--')
        }
    }, [displayEnd, monthList, isEndEditing])

    useEffect(() => {
        if (!sliderRef.current || monthList.length === 0) return

        const maxIndex = monthList.length - 1
        const validStartIndex = Math.min(Math.max(0, startIndex), maxIndex)
        const validEndIndex = Math.min(Math.max(0, endIndex), maxIndex)

        if (sliderInstance.current) {
            sliderInstance.current.destroy()
            sliderInstance.current = null
        }

        sliderInstance.current = noUiSlider.create(sliderRef.current, {
            start: [validStartIndex, validEndIndex],
            connect: true,
            step: 1,
            range: {
                min: 0,
                max: maxIndex
            },
            format: {
                to: (value: number) => Math.round(value),
                from: (value: string) => Number(value)
            }
        })

        sliderInstance.current.on('update', (values: (string | number)[]) => {
            const start = Math.round(Number(values[0]))
            const end = Math.round(Number(values[1]))
            setDisplayStart(start)
            setDisplayEnd(end)
        })

        sliderInstance.current.on('set', (values: (string | number)[]) => {
            if (isUpdatingProgrammatically.current) {
                isUpdatingProgrammatically.current = false
                return
            }
            const start = Math.round(Number(values[0]))
            const end = Math.round(Number(values[1]))
            onChange(start, end)
        })

        return () => {
            if (sliderInstance.current) {
                sliderInstance.current.destroy()
                sliderInstance.current = null
            }
        }
    }, [monthList])

    useEffect(() => {
        if (sliderInstance.current && monthList.length > 0) {
            const maxIndex = monthList.length - 1
            const validStartIndex = Math.min(Math.max(0, startIndex), maxIndex)
            const validEndIndex = Math.min(Math.max(0, endIndex), maxIndex)

            const currentValues = sliderInstance.current.get() as string[]
            const currentStart = Math.round(Number(currentValues[0]))
            const currentEnd = Math.round(Number(currentValues[1]))

            if (currentStart !== validStartIndex || currentEnd !== validEndIndex) {
                isUpdatingProgrammatically.current = true
                sliderInstance.current.set([validStartIndex, validEndIndex])
                setDisplayStart(validStartIndex)
                setDisplayEnd(validEndIndex)
            }
        }
    }, [startIndex, endIndex, monthList])

    const handleDateInputBlur = (handleIndex: 0 | 1, inputValue: string) => {
        if (handleIndex === 0) setIsStartEditing(false)
        else setIsEndEditing(false)

        if (!sliderInstance.current || !monthList.length) return

        const parsed = parseDateInput(inputValue)
        if (!parsed) {
            const values = sliderInstance.current.get() as string[]
            const idx = Math.round(Number(values[handleIndex]))
            if (handleIndex === 0) setStartInputValue(monthList[idx]?.label || '--')
            else setEndInputValue(monthList[idx]?.label || '--')
            return
        }

        const targetIdx = findClosestMonthIndex(monthList, parsed.value)
        if (targetIdx === -1) {
            const values = sliderInstance.current.get() as string[]
            const idx = Math.round(Number(values[handleIndex]))
            if (handleIndex === 0) setStartInputValue(monthList[idx]?.label || '--')
            else setEndInputValue(monthList[idx]?.label || '--')
            return
        }

        const currentValues = sliderInstance.current.get() as string[]
        const currentStart = Math.round(Number(currentValues[0]))
        const currentEnd = Math.round(Number(currentValues[1]))

        if (handleIndex === 0) {
            const newStart = Math.min(targetIdx, currentEnd)
            sliderInstance.current.set([newStart, null])
            onChange(newStart, currentEnd)
        } else {
            const newEnd = Math.max(targetIdx, currentStart)
            sliderInstance.current.set([null, newEnd])
            onChange(currentStart, newEnd)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
        }
    }

    return (
        <div className="time-filter">
            <div className="date-row">
                <input
                    className="date-box"
                    value={startInputValue}
                    size={1}
                    onChange={(e) => setStartInputValue(e.target.value)}
                    onFocus={(e) => { setIsStartEditing(true); e.target.select() }}
                    onBlur={(e) => handleDateInputBlur(0, e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <span className="arrow">&rarr;</span>
                <input
                    className="date-box"
                    value={endInputValue}
                    size={1}
                    onChange={(e) => setEndInputValue(e.target.value)}
                    onFocus={(e) => { setIsEndEditing(true); e.target.select() }}
                    onBlur={(e) => handleDateInputBlur(1, e.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>
            <div className="slider-row">
                <div ref={sliderRef} id="date-slider"></div>
            </div>
        </div>
    )
}

export default DateRangeSlider
